import axios from 'axios'
const AUTH_LOCAL_STORAGE_KEY = 'cdw-auth'

const getAuth = () => {
  if (!localStorage) {
    return
  }

  const lsValue = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth = JSON.parse(lsValue)
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export const HANDOFF_PATH = '/handoff'
export const HANDOFF_LANDING_PATH = '/admin'

// Invite and pass links that authenticate API calls with their own ?token=.
// Nowhere else may a URL token touch the session or the Authorization header:
// a link carrying someone else's token would otherwise sign the visitor in as
// them, and /reset-password must keep its token for the reset form.
const URL_TOKEN_PATHS = ['/claim-card', '/generate-pass', '/create-password']

const isUnderPath = (pathname, base) =>
  pathname === base || pathname.startsWith(base + '/')

function urlTokenForCurrentPath() {
  if (typeof window === 'undefined') return null
  const { pathname, search } = window.location
  if (!URL_TOKEN_PATHS.some((base) => isUnderPath(pathname, base))) return null
  return new URLSearchParams(search).get('token')
}

// Superadmin's "Access customer account" opens
// /handoff?token=<jwt>&merchantId=<accountId>. Runs before React mounts so
// AuthProvider's first read sees the handed-off session, which replaces any
// existing one. The URL is rewritten to the dashboard so the token does not
// linger in history, bookmarks or referrer headers. Returns the path the app
// should show, or null when the current URL is not a handoff.
function consumeHandoffToken() {
  if (typeof window === 'undefined') return null
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/'
  if (pathname !== HANDOFF_PATH) return null

  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')
  const merchantId = params.get('merchantId')
  const target = token && merchantId ? HANDOFF_LANDING_PATH : '/'

  if (token && merchantId) {
    removeAuth()
    setAuth({ token, merchantId })
  }

  window.history.replaceState({}, document.title, target)
  return target
}

// AuthInit's session check must use the visitor's own session: a URL token there
// (an invite is not a session) would fail it and sign the visitor out.
const isSessionCheck = (url) => typeof url === 'string' && /\/auth\/verify_token(\?|$)/.test(url)

function setupAxios() {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config) => {
      const auth = getAuth()
      if (auth && auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`
      }

      const urlToken = urlTokenForCurrentPath()
      if (urlToken && !isSessionCheck(config.url)) {
        config.headers.Authorization = `Bearer ${urlToken}`
      }

      return config
    },
    (err) => Promise.reject(err)
  )
}

export {
  getAuth,
  setAuth,
  removeAuth,
  AUTH_LOCAL_STORAGE_KEY,
  setupAxios,
  consumeHandoffToken,
  urlTokenForCurrentPath,
}
